import { env } from "@config";

export type EncryptedAESKey = string;
export type EncryptedData = string;

export interface EncryptedPayload {
  r: [EncryptedAESKey, EncryptedData];
}

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = "";
  const chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...Array.from(bytes.subarray(i, i + chunkSize)));
  }

  return btoa(binary);
};

const base64ToBytes = (b64: string): Uint8Array => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
};

const base64UrlToBytes = (b64url: string): Uint8Array => {
  const padded = b64url
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(Math.ceil(b64url.length / 4) * 4, "=");
  return base64ToBytes(padded);
};

const concatBytes = (...chunks: Uint8Array[]): Uint8Array => {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const output = new Uint8Array(total);
  let offset = 0;

  chunks.forEach((chunk) => {
    output.set(chunk, offset);
    offset += chunk.length;
  });

  return output;
};

const importPublicKey = async (key: string): Promise<CryptoKey> =>
  window.crypto.subtle.importKey(
    "spki",
    base64ToBytes(key).buffer as ArrayBuffer,
    {
      name: "RSA-OAEP",
      hash: "SHA-1",
    },
    false,
    ["encrypt"]
  );

const importPrivateKey = async (key: string): Promise<CryptoKey> =>
  window.crypto.subtle.importKey(
    "pkcs8",
    base64ToBytes(key).buffer as ArrayBuffer,
    {
      name: "RSA-OAEP",
      hash: "SHA-1",
    },
    false,
    ["decrypt"]
  );

export const encrypt = async <T>(data: T): Promise<EncryptedPayload> => {
  const aesKeyRaw = window.crypto.getRandomValues(new Uint8Array(32));
  const nonce = window.crypto.getRandomValues(new Uint8Array(12));

  const aesKey = await window.crypto.subtle.importKey(
    "raw",
    aesKeyRaw,
    { name: "AES-GCM" },
    false,
    ["encrypt"]
  );

  const dataBuffer = textEncoder.encode(JSON.stringify(data));
  const encrypted = new Uint8Array(
    await window.crypto.subtle.encrypt(
      { name: "AES-GCM", iv: nonce, tagLength: 128 },
      aesKey,
      dataBuffer
    )
  );

  const tagLength = 16;
  const ciphertext = encrypted.slice(0, encrypted.length - tagLength);
  const tag = encrypted.slice(encrypted.length - tagLength);

  const version = textEncoder.encode("GCM1");
  const payload = concatBytes(version, nonce, tag, ciphertext);
  const encryptedPayload = bytesToBase64(payload);

  const aesKeyB64 = bytesToBase64(aesKeyRaw);
  const rsaKey = await importPublicKey(env.encryption.publicKey);
  const encryptedKeyBytes = new Uint8Array(
    await window.crypto.subtle.encrypt({ name: "RSA-OAEP" }, rsaKey, textEncoder.encode(aesKeyB64))
  );
  const encryptedAesKey = bytesToBase64(encryptedKeyBytes);

  return {
    r: [encryptedAesKey, encryptedPayload],
  };
};

export const decrypt = async <T>(data: EncryptedPayload): Promise<T> => {
  const [encryptedKey, encryptedPayload] = data.r;

  const rsaKey = await importPrivateKey(env.encryption.privateKey);
  const encryptedKeyBytes = base64ToBytes(encryptedKey);
  const decryptedKeyBytes = new Uint8Array(
    await window.crypto.subtle.decrypt(
      { name: "RSA-OAEP" },
      rsaKey,
      encryptedKeyBytes.buffer as ArrayBuffer
    )
  );
  const aesKeyB64Url = textDecoder.decode(decryptedKeyBytes);

  const payloadBytes = base64ToBytes(encryptedPayload);
  const version = textDecoder.decode(payloadBytes.slice(0, 4));

  if (version !== "GCM1") {
    throw new Error("Unsupported AES payload version.");
  }

  const nonce = payloadBytes.slice(4, 16);
  const tag = payloadBytes.slice(16, 32);
  const ciphertext = payloadBytes.slice(32);

  const encryptedCombined = concatBytes(ciphertext, tag);

  const aesKeyBytes = base64UrlToBytes(aesKeyB64Url);
  const aesKey = await window.crypto.subtle.importKey(
    "raw",
    aesKeyBytes.buffer as ArrayBuffer,
    { name: "AES-GCM" },
    false,
    ["decrypt"]
  );

  const plaintextBytes = new Uint8Array(
    await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv: nonce, tagLength: 128 },
      aesKey,
      encryptedCombined.buffer as ArrayBuffer
    )
  );

  return JSON.parse(textDecoder.decode(plaintextBytes)) as T;
};
